/**
 * FormModal — Layer 3 template for form AND detail modals.
 * Composes BaseModal (fullscreen mobile) + header + scrollable body + optional footer.
 * Footer: desktop = submit button, mobile = SwipeToSubmit.
 * When onSubmit is omitted the footer is not rendered (view-only mode).
 */

import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { XIcon, Loader2Icon } from 'lucide-react';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { BaseModal, useModalClose } from './BaseModal';

/** Close button that uses animated close when available (mobile) */
function ModalCloseButton({ onClose, disabled, isMobile, isRTL, isDarkHeader }: {
  onClose: () => void; disabled: boolean; isMobile: boolean; isRTL: boolean; isDarkHeader: boolean;
}) {
  const animatedClose = useModalClose();
  const handleClose = animatedClose || onClose;
  return (
    <button
      onClick={handleClose}
      disabled={disabled}
      className={
        isMobile
          ? 'absolute rounded-full transition-all active:translate-y-0.5 disabled:opacity-50'
          : 'absolute flex items-center justify-center w-8 h-8 rounded-full transition-transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed'
      }
      style={{
        [isRTL ? 'left' : 'right']: isMobile ? 16 : 12,
        ...(isMobile
          ? {
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDarkHeader ? 'rgba(255,255,255,0.2)' : MODAL_COLORS.surface,
              border: `1px solid ${isDarkHeader ? 'rgba(255,255,255,0.3)' : MODAL_COLORS.border}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }
          : {
              backgroundColor: MODAL_COLORS.interactive,
              border: `1px solid ${MODAL_COLORS.border}`,
            }),
      }}
      aria-label="Close"
    >
      <XIcon className="h-4 w-4" style={{ color: isDarkHeader ? '#ffffff' : MODAL_COLORS.text }} />
    </button>
  );
}
import { SwipeToSubmit } from './SwipeToSubmit';
import { MODAL_COLORS } from './constants';
import type { FormModalProps } from './types';

export function FormModal({
  isOpen,
  onClose,
  title,
  isPending = false,
  isAnimating = false,
  onSubmit,
  onValidate,
  isFormValid = true,
  modalContentRef,
  maxWidth,
  onExitComplete,
  submitLabel,
  error,
  customFooter,
  preventClose,
  headerStyle: headerStyleProp,
  children,
}: FormModalProps) {
  const { t, i18n } = useTranslation();
  const isRTL =
    typeof document !== 'undefined' ? document.documentElement.dir === 'rtl' : false;
  const isHebrew = i18n.language === 'he';
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const isSubmitDisabled = isPending || isAnimating;
  const hasSubmit = !!onSubmit;
  const isDarkHeader = headerStyleProp?.color === '#ffffff';

  return (
    <BaseModal
      ref={modalContentRef}
      isOpen={isOpen}
      onClose={onClose}
      preventClose={preventClose || (hasSubmit ? isSubmitDisabled : false)}
      mobileVariant="fullscreen"
      maxWidth={maxWidth}
      ariaLabel={title}
      onExitComplete={onExitComplete}
      flushTop={!!headerStyleProp}
    >
      {/* Header — drag handle + title + close button */}
      <div
        className="flex-shrink-0"
        style={{
          backgroundColor: headerStyleProp ? undefined : 'rgba(255, 255, 255, 0.7)',
          borderBottom: headerStyleProp ? undefined : '1px solid rgba(255, 255, 255, 0.4)',
          ...(isMobile && !headerStyleProp
            ? {
                backdropFilter: 'blur(40px) saturate(180%)',
                WebkitBackdropFilter: 'blur(40px) saturate(180%)',
              }
            : {}),
          ...headerStyleProp,
          paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : undefined,
        }}
      >
        {/* Drag handle (mobile only) */}
        {isMobile && (
          <div className="flex justify-center pt-2 pb-1">
            <div
              className="w-12 h-1.5 rounded-full"
              style={{ backgroundColor: isDarkHeader ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.15)', opacity: isDarkHeader ? 0.3 : 1 }}
            />
          </div>
        )}

        <div className="flex items-center justify-center relative px-4 py-3">
          <h1
            style={{
              color: headerStyleProp?.color ?? MODAL_COLORS.text,
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
            }}
          >
            {title}
          </h1>
          <ModalCloseButton
            onClose={onClose}
            disabled={hasSubmit ? isSubmitDisabled : false}
            isMobile={isMobile}
            isRTL={isRTL}
            isDarkHeader={isDarkHeader}
          />
        </div>
      </div>

      {/* Scrollable body */}
      <div
        className={`flex-1 overflow-y-auto hide-scrollbar ${isMobile ? 'p-4' : 'p-3'}`}
        style={{
          overscrollBehaviorY: 'contain',
          ...(headerStyleProp ? {
            backgroundColor: 'rgba(255,255,255,0.55)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          } : {}),
        }}
      >
        {children}

        {/* Error message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-3 rounded-xl text-center font-bold mt-3"
            style={{
              backgroundColor: '#FEE2E2',
              border: `1px solid ${MODAL_COLORS.border}`,
              color: '#B91C1C',
            }}
          >
            {error}
          </motion.div>
        )}
      </div>

      {/* Footer — customFooter overrides default, otherwise render submit button / swipe */}
      {customFooter ? (
        <div
          className="flex-shrink-0 p-3"
          style={{
            borderTop: 'none',
            ...(isMobile ? { paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' } : {}),
            ...(headerStyleProp ? {
              backgroundColor: 'rgba(255,255,255,0.55)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            } : {}),
          }}
        >
          {customFooter}
        </div>
      ) : hasSubmit && (
        isMobile ? (
          <div
            className="flex-shrink-0 p-4"
            style={{
              borderTop: 'none',
              paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))',
              touchAction: 'none',
              ...(headerStyleProp ? {
                backgroundColor: 'rgba(255,255,255,0.55)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              } : {}),
            }}
          >
            <SwipeToSubmit
              onValidate={onValidate || (() => true)}
              onSubmit={onSubmit}
              disabled={isSubmitDisabled}
              isPending={isPending || isAnimating}
              isFormValid={isFormValid}
              isHebrew={isHebrew}
              isRTL={isRTL}
            />
          </div>
        ) : (
          <div
            className="p-3 flex-shrink-0"
            style={{ borderTop: `1px solid ${MODAL_COLORS.border}` }}
          >
            <button
              onClick={onSubmit}
              disabled={isSubmitDisabled}
              className="w-full py-3 rounded-xl font-semibold transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{
                backgroundColor: MODAL_COLORS.header,
                color: '#ffffff',
                border: `1px solid ${MODAL_COLORS.border}`,
                boxShadow: isSubmitDisabled
                  ? 'none'
                  : '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              {isPending || isAnimating ? (
                <>
                  <Loader2Icon className="h-5 w-5 animate-spin" />
                  {isHebrew ? '' : 'Submitting...'}
                </>
              ) : (
                submitLabel || t('common.save')
              )}
            </button>
          </div>
        )
      )}
    </BaseModal>
  );
}
